import { pxToRem } from 'utils/cssUnits';

/**
 * In the category sheet (OP-2556) a task's first product row also holds the
 * Task cell, which spans every row, so the browser's own drag picture of a
 * row takes in part of the Task cell. This builds the picture from the
 * product cells alone: a copy of the row without the Task cell, in a table of
 * the same cell widths, placed off screen for the moment the browser
 * snapshots it. The measured widths are runtime values, so they go through
 * CSS custom properties in rem to `.task-sheet-drag-image` in App.css, as
 * the step picker flyout's position does.
 */
export const setSheetRowDragImage = (
  event: React.DragEvent<HTMLTableRowElement>
): void => {
  if (!event.dataTransfer?.setDragImage) return;
  const row = event.currentTarget;
  const cells = Array.from(row.cells).filter(
    (cell) => !cell.classList.contains('task-sheet-task-cell')
  );
  if (cells.length === 0) return;

  const table = document.createElement('table');
  table.className =
    'task-sheet-drag-image border-collapse bg-white text-base text-gray-dark';
  const copy = document.createElement('tr');
  let width = 0;
  cells.forEach((cell) => {
    const cellCopy = cell.cloneNode(true) as HTMLTableCellElement;
    const cellWidth = cell.getBoundingClientRect().width;
    width += cellWidth;
    cellCopy.classList.add('task-sheet-drag-cell');
    cellCopy.style.setProperty('--drag-cell-width', `${pxToRem(cellWidth)}rem`);
    copy.appendChild(cellCopy);
  });
  copy.classList.add('task-sheet-drag-row');
  copy.style.setProperty(
    '--drag-row-height',
    `${pxToRem(row.getBoundingClientRect().height)}rem`
  );
  table.style.setProperty('--drag-table-width', `${pxToRem(width)}rem`);
  table.appendChild(copy);
  document.body.appendChild(table);

  const firstCell = cells[0].getBoundingClientRect();
  event.dataTransfer.setDragImage(
    table,
    event.clientX - firstCell.left,
    event.clientY - firstCell.top
  );
  // The browser takes its picture synchronously; the copy can go next tick.
  window.setTimeout(() => table.remove(), 0);
};
